import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MoneyOffOutlinedIcon from '@mui/icons-material/MoneyOffOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_LOWEST,
} from '../brandDashboardTokens';
import type { CampaignHostTierFeatureKey, CampaignHostTierFeatureRow } from './campaignHostTierFeatures';
import { getSuiteFeature } from './campaignHostTierFeatures';

const GOLD = '#FFC107';

const FEATURE_ICONS: Record<CampaignHostTierFeatureKey, React.ElementType> = {
    unlimited: RocketLaunchIcon,
    saveFees: SavingsOutlinedIcon,
    zeroFees: MoneyOffOutlinedIcon,
    priority: TrendingUpIcon,
    hireCreators: GroupsOutlinedIcon,
    hireCreators50: GroupsOutlinedIcon,
    instagramAutomation: AutoAwesomeOutlinedIcon,
    smsLiveUrl: SmsOutlinedIcon,
};

export type CampaignHostSuiteFeatureListVariant = 'tier' | 'locked' | 'active';

export interface CampaignHostSuiteFeatureListProps {
    rows: CampaignHostTierFeatureRow[];
    variant?: CampaignHostSuiteFeatureListVariant;
    featured?: boolean;
}

export function CampaignHostSuiteFeatureList({
    rows,
    variant = 'tier',
    featured = false,
}: CampaignHostSuiteFeatureListProps) {
    const isLocked = variant === 'locked';
    const isActive = variant === 'active';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isLocked || isActive ? 2 : 1.5 }}>
            {rows.map(row => {
                const meta = getSuiteFeature(row.key);
                const included = row.included;
                const Icon = FEATURE_ICONS[row.key];
                const showLock = !included && !isActive;

                return (
                    <Box
                        key={row.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            p: isLocked || isActive ? 2.5 : 0,
                            borderRadius: isLocked || isActive ? 3 : 0,
                            bgcolor: isLocked
                                ? BRAND_SURFACE_CONTAINER_LOWEST
                                : isActive
                                ? BRAND_SURFACE_CONTAINER_LOW
                                : 'transparent',
                            filter: isLocked ? 'grayscale(1)' : 'none',
                            opacity: isLocked ? 0.55 : isActive && !included ? 0.65 : included ? 1 : 0.35,
                        }}>
                        {isLocked || isActive ? (
                            <Box
                                sx={{
                                    p: 1.25,
                                    borderRadius: '50%',
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    flexShrink: 0,
                                }}>
                                <Icon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 26 }} />
                            </Box>
                        ) : showLock ? (
                            <LockOutlinedIcon sx={{ fontSize: 18, color: '#666', mt: 0.25, flexShrink: 0 }} />
                        ) : (
                            <CheckCircleIcon
                                sx={{
                                    fontSize: 20,
                                    color: GOLD,
                                    mt: 0.25,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: isLocked || isActive ? 16 : 14,
                                    fontWeight: included && (featured || isActive) ? 600 : 500,
                                    color: included ? '#fff' : '#adaaaa',
                                    lineHeight: 1.3,
                                }}>
                                {meta.title}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#adaaaa',
                                    mt: 0.5,
                                    lineHeight: 1.45,
                                }}>
                                {meta.description}
                            </Typography>
                        </Box>
                        {isLocked && <LockOutlinedIcon sx={{ color: BRAND_OUTLINE_VARIANT, flexShrink: 0 }} />}
                        {isActive && included && (
                            <CheckCircleIcon sx={{ color: BRAND_LUXE_SECONDARY, flexShrink: 0, mt: 0.5 }} />
                        )}
                        {isActive && !included && (
                            <LockOutlinedIcon sx={{ color: BRAND_OUTLINE_VARIANT, flexShrink: 0, mt: 0.5 }} />
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}
